import React, { useState } from 'react';

// 通用表格组件
// 通用表格组件
// 通用表格组件
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    // 强制使用中国标准时间 (Asia/Shanghai)
    const options = {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    };
    // zh-CN 通常显示 yyyy/mm/dd HH:mm:ss
    // 这里的 replace 用于标准化分隔符
    return date.toLocaleString('zh-CN', options).replace(/\//g, '-');
  } catch (e) {
    return dateStr || '-';
  }
};

// 通用表格组件
export const Table = ({ columns, data, loading, emptyText = '暂无数据', rowSelection, sort, onSort }) => {
  if (loading) {
    return <div className="table-loading">加载中...</div>;
  }

  if (!data || data.length === 0) {
    return <div className="table-empty">{emptyText}</div>;
  }

  const handleSelectAll = (e) => {
    if (!rowSelection) return;
    if (e.target.checked) {
      const allIds = data.map(item => item.id);
      rowSelection.onChange(allIds);
    } else {
      rowSelection.onChange([]);
    }
  };

  const handleSelectRow = (id, checked) => {
    if (!rowSelection) return;
    const selected = rowSelection.selectedRowKeys || [];
    if (checked) {
      rowSelection.onChange([...selected, id]);
    } else {
      rowSelection.onChange(selected.filter(key => key !== id));
    }
  };

  const isAllSelected = rowSelection && data.length > 0 && data.every(item => rowSelection.selectedRowKeys.includes(item.id));

  const handleHeaderClick = (column) => {
    if (!column.sorter || !onSort) return;

    // 切换排序逻辑: 默认/desc -> asc -> desc
    let newOrder = 'desc';
    if (sort && sort.field === column.key) {
      newOrder = sort.order === 'asc' ? 'desc' : 'asc';
    }
    onSort(column.key, newOrder);
  };

  return (
    <table className="table">
      <thead>
        <tr>
          {rowSelection && (
            <th style={{ width: '40px', textAlign: 'center' }}>
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={handleSelectAll}
              />
            </th>
          )}
          {columns.map((col) => (
            <th
              key={col.key}
              style={{ width: col.width, cursor: col.sorter ? 'pointer' : 'default' }}
              onClick={() => handleHeaderClick(col)}
              className={col.sorter ? 'sortable-header' : ''}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {col.title}
                {col.sorter && sort && sort.field === col.key && (
                  <span style={{ fontSize: '10px' }}>
                    {sort.order === 'asc' ? '▲' : '▼'}
                  </span>
                )}
                {col.sorter && (!sort || sort.field !== col.key) && (
                  <span style={{ fontSize: '10px', color: '#ccc' }}>⇅</span>
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, index) => (
          <tr key={row.id || index}>
            {rowSelection && (
              <td style={{ textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={rowSelection.selectedRowKeys.includes(row.id)}
                  onChange={(e) => handleSelectRow(row.id, e.target.checked)}
                />
              </td>
            )}
            {columns.map((col) => (
              <td key={col.key}>
                {col.render ? col.render(row[col.key], row) : row[col.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// 分页组件
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export const Pagination = ({ current, total, pageSize, onChange, onPageSizeChange }) => {
  const totalPages = Math.ceil(total / pageSize) || 1;

  // 当只有1页且没有pageSize选择器时才隐藏
  if (totalPages <= 1 && !onPageSizeChange) return null;

  return (
    <div className="pagination">
      <button
        disabled={current === 1}
        onClick={() => onChange(current - 1)}
      >
        上一页
      </button>
      <span className="pagination-info">
        第 {current} 页 / 共 {totalPages} 页 (共 {total} 条)
      </span>
      <button
        disabled={current === totalPages}
        onClick={() => onChange(current + 1)}
      >
        下一页
      </button>
      {onPageSizeChange && (
        <select
          className="pagination-size-select"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {PAGE_SIZE_OPTIONS.map(size => (
            <option key={size} value={size}>{size} 条/页</option>
          ))}
        </select>
      )}
    </div>
  );
};

// 模态框组件
export const Modal = ({ visible, title, onClose, onConfirm, children, footer, width }) => {
  if (!visible) return null;

  // 如果提供了 onConfirm 但没有自定义 footer，则自动生成确认/取消按钮
  const defaultFooter = onConfirm && !footer ? (
    <div className="modal-footer">
      <button className="btn-secondary" onClick={onClose}>取消</button>
      <button className="btn-primary" onClick={onConfirm}>确认</button>
    </div>
  ) : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={width ? { width } : {}}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer || defaultFooter}
      </div>
    </div>
  );
};

// 消息提示
export const message = {
  success: (text) => {
    showMessage(text, 'success');
  },
  error: (text) => {
    showMessage(text, 'error');
  },
  info: (text) => {
    showMessage(text, 'info');
  },
};

function showMessage(text, type) {
  const container = document.getElementById('message-container') || createMessageContainer();
  const messageEl = document.createElement('div');
  messageEl.className = `message message-${type}`;
  messageEl.textContent = text;
  container.appendChild(messageEl);

  setTimeout(() => {
    messageEl.classList.add('message-fade-out');
    setTimeout(() => {
      container.removeChild(messageEl);
    }, 300);
  }, 3000);
}

function createMessageContainer() {
  const container = document.createElement('div');
  container.id = 'message-container';
  container.className = 'message-container';
  document.body.appendChild(container);
  return container;
}

// 确认对话框
export const confirm = (message) => {
  return window.confirm(message);
};

// 统计卡片
export const StatCard = ({ title, value, color = '#1890ff' }) => {
  return (
    <div className="stat-card" style={{ borderTopColor: color }}>
      <div className="stat-value">{value}</div>
      <div className="stat-title">{title}</div>
    </div>
  );
};

// 表单输入组件
export const FormItem = ({ label, required, children, error }) => {
  return (
    <div className="form-item">
      <label className="form-label">
        {required && <span className="required">*</span>}
        {label}
      </label>
      {children}
      {error && <span className="form-error">{error}</span>}
    </div>
  );
};

// 搜索栏
export const SearchBar = ({ children, onSearch }) => {
  return (
    <div className="search-bar">
      {children}
      <button className="btn btn-primary" onClick={onSearch}>
        搜索
      </button>
    </div>
  );
};

// 页面头部
export const PageHeader = ({ title, extra }) => {
  return (
    <div className="page-header">
      <h1>{title}</h1>
      {extra && <div className="page-header-extra">{extra}</div>}
    </div>
  );
};

export const Tooltip = ({ text, children }) => {
  return (
    <div className="tooltip-container">
      {children}
      <span className="tooltip-text">{text}</span>
    </div>
  );
};

export { default as VersionModal } from './VersionModal';
